import { useState, useEffect } from 'react';
import { STORAGE_KEYS } from '../utils/constants';

export interface UseFirstTimeUserReturn {
  isFirstRun: boolean;
  hasSeenWelcomeTour: boolean;
  hasSeenNote: (noteId: string) => boolean;
  markNoteSeen: (noteId: string) => void;
  completeWelcomeTour: () => void;
  resetFirstRun: () => void;
}

export const useFirstTimeUser = (): UseFirstTimeUserReturn => {
  const [isFirstRun, setIsFirstRun] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.IS_FIRST_RUN);
    // If never set before, it's a first run
    return stored === null || stored === 'true';
  });

  const [hasSeenWelcomeTour, setHasSeenWelcomeTour] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.WELCOME_TOUR_COMPLETED);
    return stored === 'true';
  });

  const [dismissedNotes, setDismissedNotes] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.DISMISSED_NOTES);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error parsing dismissed notes:', error);
      return [];
    }
  });

  // Sync state to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.IS_FIRST_RUN, isFirstRun.toString());
  }, [isFirstRun]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.WELCOME_TOUR_COMPLETED, hasSeenWelcomeTour.toString());
  }, [hasSeenWelcomeTour]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.DISMISSED_NOTES, JSON.stringify(dismissedNotes));
  }, [dismissedNotes]);

  const hasSeenNote = (noteId: string): boolean => {
    return dismissedNotes.includes(noteId);
  };

  const markNoteSeen = (noteId: string): void => {
    if (!dismissedNotes.includes(noteId)) {
      setDismissedNotes(prev => [...prev, noteId]);
    }
  };

  const completeWelcomeTour = (): void => {
    setHasSeenWelcomeTour(true);
    setIsFirstRun(false);
  };

  const resetFirstRun = (): void => {
    setIsFirstRun(true);
    setHasSeenWelcomeTour(false);
    setDismissedNotes([]);
    localStorage.removeItem(STORAGE_KEYS.IS_FIRST_RUN);
    localStorage.removeItem(STORAGE_KEYS.WELCOME_TOUR_COMPLETED);
    localStorage.removeItem(STORAGE_KEYS.DISMISSED_NOTES);
  };

  return {
    isFirstRun,
    hasSeenWelcomeTour,
    hasSeenNote,
    markNoteSeen,
    completeWelcomeTour,
    resetFirstRun,
  };
};
